import { HolderOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Select } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import { createSortableList } from '../../../utils/createSortableList';
import {
  createExportFolderFileListDateFormatter,
  EXPORT_FOLDER_FILE_LIST_COLUMNS,
  EXPORT_FOLDER_FILE_LIST_SAMPLE_DATE_TICKS,
  EXPORT_FOLDER_FILE_LIST_TAGS_JOIN,
  getDefaultSelectedExportFolderFileListColumns,
  type ExportFolderFileListColumn,
  type ExportFolderFileListDateFormat,
  type ExportFolderFileListDelimiter,
  type ExportFolderFileListFormat,
  type ExportFolderFileListTagJoin,
} from '../../../utils/exportFolderFileListCsv/exportFolderFileListCsv';

export type ExportFolderFileListFormResult = {
  columns: ExportFolderFileListColumn[];
  delimiter: ExportFolderFileListDelimiter;
  tagsJoin: ExportFolderFileListTagJoin;
  dateFormat: ExportFolderFileListDateFormat;
};

type Props = {
  /** Preselected export format from the dropdown (controls which options show). */
  format: ExportFolderFileListFormat;
  /** Current files section — Received defaults createdBy/sharedBy checked. */
  section?: FilesTableEnum;
  disabled?: boolean;
  onCancel: () => void;
  onExport: (result: ExportFolderFileListFormResult) => void;
};

/**
 * Export options body for `modal.confirm` (footer: null).
 * Sortable column list via SortableJS; delimiter/tagsJoin only for CSV.
 */
const ExportFolderFileListForm = ({
  format,
  section,
  disabled = false,
  onCancel,
  onExport,
}: Props) => {
  const { t, i18n } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const orderRef = useRef<ExportFolderFileListColumn[]>([...EXPORT_FOLDER_FILE_LIST_COLUMNS]);
  const dragStartOrderRef = useRef<ExportFolderFileListColumn[]>([]);
  const [orderedColumns, setOrderedColumns] = useState<ExportFolderFileListColumn[]>([
    ...EXPORT_FOLDER_FILE_LIST_COLUMNS,
  ]);
  const [selectedColumns, setSelectedColumns] = useState<Set<ExportFolderFileListColumn>>(
    () => new Set(getDefaultSelectedExportFolderFileListColumns(section)),
  );
  const [delimiter, setDelimiter] = useState<ExportFolderFileListDelimiter>(',');
  const [tagsJoin, setTagsJoin] = useState<ExportFolderFileListTagJoin>(
    EXPORT_FOLDER_FILE_LIST_TAGS_JOIN,
  );
  const [dateFormat, setDateFormat] = useState<ExportFolderFileListDateFormat>('localeShort');
  const [sortableEpoch, setSortableEpoch] = useState(0);

  orderRef.current = orderedColumns;

  const showDelimiterOptions = format === 'csv';

  const labelByKey = useMemo(
    (): Record<ExportFolderFileListColumn, string> => ({
      id: t('files.exportFolderFileList.id'),
      name: t('common.fileName'),
      type: t('files.exportFolderFileList.extension'),
      size: t('common.fileSize'),
      lastModified: t('common.lastModified'),
      fileType: t('common.fileType'),
      isShared: t('files.exportFolderFileList.isShared'),
      createdBy: t('common.createdBy'),
      sharedBy: t('common.sharedBy'),
      tags: t('common.tags'),
    }),
    [t],
  );

  const delimiterOptions = useMemo(
    () => [
      { value: ',' as const, label: t('files.exportFolderFileList.delimiterComma') },
      { value: ';' as const, label: t('files.exportFolderFileList.delimiterSemicolon') },
      { value: '\t' as const, label: t('files.exportFolderFileList.delimiterTab') },
      { value: '|' as const, label: t('files.exportFolderFileList.delimiterPipe') },
    ],
    [t],
  );

  const tagsJoinOptions = useMemo(
    () => [
      { value: ';' as const, label: t('files.exportFolderFileList.tagsJoinSemicolon') },
      { value: '|' as const, label: t('files.exportFolderFileList.tagsJoinPipe') },
      { value: ',' as const, label: t('files.exportFolderFileList.tagsJoinComma') },
    ],
    [t],
  );

  const dateFormatOptions = useMemo(() => {
    const sample = (formatPreset: ExportFolderFileListDateFormat) =>
      createExportFolderFileListDateFormatter(
        formatPreset,
        i18n.language,
      )(EXPORT_FOLDER_FILE_LIST_SAMPLE_DATE_TICKS);

    return (
      [
        'localeShort',
        'iso',
        'localeDateTime',
        'dmy',
        'mdy',
        'isoDateTime',
        'europeanDateTime',
      ] as const
    ).map((value) => ({
      value,
      label: t(`files.exportFolderFileList.dateFormat.${value}`, {
        sample: sample(value),
      }),
    }));
  }, [t, i18n.language]);

  const tagsJoinConflictsWithDelimiter = showDelimiterOptions && tagsJoin === delimiter;

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement || orderedColumns.length < 2) return;

    let destroyedOnEnd = false;
    const sortable = createSortableList(listElement, {
      handle: '.export-col-drag-handle',
      draggable: '[data-sortable-id]',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      fallbackClass: 'sortable-fallback',
      onStart: () => {
        dragStartOrderRef.current = orderRef.current.slice();
      },
      onEnd: () => {
        const nextOrder = Array.from(listElement.children)
          .filter((child): child is HTMLElement => child instanceof HTMLElement)
          .map((child) => child.dataset.sortableId as ExportFolderFileListColumn)
          .filter((column): column is ExportFolderFileListColumn =>
            EXPORT_FOLDER_FILE_LIST_COLUMNS.includes(column),
          );

        // Restore DOM before React reconciles (anti removeChild NotFoundError).
        destroyedOnEnd = true;
        sortable.destroy();
        const elementByColumnId = new Map<string, HTMLElement>();
        for (const child of Array.from(listElement.children)) {
          if (!(child instanceof HTMLElement) || !child.dataset.sortableId) continue;
          elementByColumnId.set(child.dataset.sortableId, child);
        }
        for (const child of Array.from(listElement.children)) {
          if (child instanceof HTMLElement && child.dataset.sortableId) child.remove();
        }
        for (const columnId of dragStartOrderRef.current) {
          const node = elementByColumnId.get(columnId);
          if (node) listElement.appendChild(node);
        }

        if (nextOrder.length === 0 || nextOrder.join(',') === dragStartOrderRef.current.join(',')) {
          setSortableEpoch((previousEpoch) => previousEpoch + 1);
          return;
        }

        setOrderedColumns(nextOrder);
        setSortableEpoch((previousEpoch) => previousEpoch + 1);
      },
    });

    return () => {
      if (!destroyedOnEnd) sortable.destroy();
    };
  }, [orderedColumns, sortableEpoch]);

  const selectedCount = orderedColumns.filter((column) => selectedColumns.has(column)).length;
  const canExport = selectedCount > 0 && !disabled;

  const toggleColumn = (column: ExportFolderFileListColumn, checked: boolean) => {
    setSelectedColumns((previousSelected) => {
      const nextSelected = new Set(previousSelected);
      if (checked) nextSelected.add(column);
      else nextSelected.delete(column);
      return nextSelected;
    });
  };

  const handleExport = () => {
    const columns = orderedColumns.filter((column) => selectedColumns.has(column));
    if (columns.length === 0 || disabled) return;
    onExport({ columns, delimiter, tagsJoin, dateFormat });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-gray-dark mb-0 text-sm">
        {t('files.exportFolderFileList.modalDescription')}
      </p>

      {showDelimiterOptions ? (
        <>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="export-csv-delimiter">
              {t('files.exportFolderFileList.delimiterLabel')}
            </label>
            <Select
              id="export-csv-delimiter"
              value={delimiter}
              options={delimiterOptions}
              onChange={setDelimiter}
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="export-csv-tags-join">
              {t('files.exportFolderFileList.tagsJoinLabel')}
            </label>
            <Select
              id="export-csv-tags-join"
              value={tagsJoin}
              options={tagsJoinOptions}
              onChange={setTagsJoin}
              className="w-full"
            />
            {tagsJoinConflictsWithDelimiter ? (
              <Alert
                type="warning"
                showIcon
                className="mt-1!"
                title={t('files.exportFolderFileList.tagsJoinConflictWarning')}
              />
            ) : null}
          </div>
        </>
      ) : null}

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium" htmlFor="export-csv-date-format">
          {t('files.exportFolderFileList.dateFormatLabel')}
        </label>
        <Select
          id="export-csv-date-format"
          value={dateFormat}
          options={dateFormatOptions}
          onChange={setDateFormat}
          className="w-full"
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{t('files.exportFolderFileList.columnsLabel')}</span>
        <div
          ref={listRef}
          className="border-gray-medium max-h-64 overflow-y-auto rounded border px-1 py-1"
        >
          {orderedColumns.map((column) => (
            <div
              key={column}
              data-sortable-id={column}
              className="flex items-center gap-1 px-1 py-1"
            >
              <Button
                type="text"
                size="small"
                className="export-col-drag-handle shrink-0 cursor-grab! px-1!"
                icon={<HolderOutlined />}
                aria-label={t('files.exportFolderFileList.dragToReorder', {
                  column: labelByKey[column],
                })}
              />
              <Checkbox
                checked={selectedColumns.has(column)}
                onChange={(event) => toggleColumn(column, event.target.checked)}
              >
                {labelByKey[column]}
              </Checkbox>
            </div>
          ))}
        </div>
      </div>

      <div className="border-gray-medium flex flex-row items-center justify-end gap-2 border-t pt-2">
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="primary" onClick={handleExport} disabled={!canExport}>
          {t('files.exportFolderFileList.confirm')}
        </Button>
      </div>
    </div>
  );
};

export default ExportFolderFileListForm;
